import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'

import { useAnalytics, versionedRoutes } from '@sketch/modules-common'
import type { GoToArtboardEventActionType } from '@sketch/canvas'

import { useCanvasScope } from '../../canvas/scope'
import { useVersioning } from '../ShareVersionContext'
import { useCompare } from './CompareContext'
import { EntityChange } from './layerTypes'

/**
 * Opens an entity absent from the canvas in the other version, which has it.
 * The comparison carries on, against the version that was on screen.
 */
export function useOpenChangedEntity() {
  const history = useHistory()
  const { share } = useVersioning()
  const scope = useCanvasScope()
  const { trackEvent } = useAnalytics()
  const versions = useCompare()?.versions ?? null

  // None in a Preview, which never compares
  const shareID = share?.identifier
  const pageUUID = scope?.pageUUID

  return useCallback(
    (change: EntityChange, actionType?: GoToArtboardEventActionType) => {
      if (!versions || !shareID || !pageUUID) return

      const [onScreen, other] = versions.isViewingNewer
        ? [versions.newer, versions.older]
        : [versions.older, versions.newer]
      const versionShortId = other.shortId

      const pathname =
        change.entityKind === 'section'
          ? versionedRoutes.PAGE_SECTION.VERSION.create({
              shareID,
              versionShortId,
              pageUUID,
              sectionUUID: change.uuid,
            })
          : versionedRoutes.FRAME.VERSION.create({
              shareID,
              versionShortId,
              frameUUID: change.uuid,
            })

      if (actionType) {
        trackEvent('CANVAS - open artboard', { actionType })
      }

      history.push({
        pathname,
        search: new URLSearchParams({ compare: onScreen.shortId }).toString(),
      })
    },
    [history, shareID, pageUUID, versions, trackEvent]
  )
}
